import React, { useEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

const MicroChart = ({ data, width = 100, height = 50, chartType = 'line' }) => {
  const chartDivRef = useRef(null);

  useEffect(() => {
    // Create root element
    const root = am5.Root.new(chartDivRef.current);

    // Remove amCharts logo
    root._logo.dispose();

    // Create the chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        pinchZoomX: false,
        interactionsEnabled: true,
        width: am5.percent(100),
        height: am5.percent(100),
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: -10,
      })
    );

    // Create X-axis
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'category',
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 10,
        }),
      })
    );

    xAxis.get('renderer').labels.template.setAll({ visible: false });
    xAxis.get('renderer').grid.template.setAll({ visible: false });

    // Create Y-axis
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0
      })
    );

    yAxis.get('renderer').labels.template.setAll({ visible: false });
    yAxis.get('renderer').grid.template.setAll({ visible: false });

    // Choose series type based on chartType prop
    let series;
    if (chartType === 'bar') {
      // Bar chart (ColumnSeries)
      series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis,
          yAxis,
          valueYField: 'value',
          categoryXField: 'category',
        })
      );

      data.forEach(item => {
        if (item.value < 0) {
          item.value = 0; // Set negative values and small negative values close from 0 to 0
        }else if(item.value === "") {
          item.value = 0; // Set empty/blank values and small negative values close from "" to 0
        }
      });

      series.data.setAll(data);

      series.columns.template.adapters.add("valueY", function(valueY, target) {
        // Ensure negative values are not shown
        if (valueY < 0) {
          return 0; // Set valueY to 0 for negative values
        }
        return valueY;
      });

    } else if (chartType === 'line') {
      // Line chart (LineSeries)
      series = chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis,
          yAxis,
          valueYField: 'value',
          categoryXField: 'category',
        })
      );
    }

    // Add tooltips
    series.set('tooltip', am5.Tooltip.new(root, {
      labelText: '[bold]{categoryX}[/]: {valueY}',
    }));

    // Set data
    xAxis.data.setAll(data);
    const filteredData = data.filter(item => !isNaN(item.value));
    series.data.setAll(filteredData);

    // Reset Icons apears if all data same and height is small (Seems Amchart bug). So reseting it manually.
    setTimeout(() => {
      chart.zoomOut();  // Reset zoom level
    }, 300)
    // Reset Icons apears if all data same and height is small (Seems Amchart bug). So reseting it manually.

    // Cleanup
    return () => {
      root.dispose();
    };
  }, [data, chartType]);

  return <div ref={chartDivRef} className="micro_chart_block" style={{ width, height }} />;
};

export default MicroChart;
